import {fetchCAPI} from '../utils/fetch-capi'
import {taskApiPath} from '../utils/api-url-helpers'
import {useCAPIQuery} from './useCAPIQuery'
import type {HydratedAPITask, RawHydratedAPITask} from '../types/api-task'
import {normalizeHydratedAPITask} from '../types/api-task'
import {useCallback} from 'react'
import {useQueryClient} from '@github-ui/react-query'
import {useDebounce} from '@github-ui/use-debounce'
import {useAgentSessionsAlive, type AgentSessionAlivePayload} from './use-agent-sessions-alive'
import {isFeatureEnabled} from '@github-ui/feature-flags'

export const agentTaskQueryKey = (owner?: string, repo?: string, taskId?: string) => ['agent-task', taskId, owner, repo]

export const fetchTask = async ({owner, repo, taskId}: {owner: string; repo: string; taskId: string}) => {
  const response = await fetchCAPI({
    path: taskApiPath(owner, repo, taskId),
  })
  const data = await response.json()
  // Handle both wrapped { task: {...} } (legacy) and unwrapped root-level response
  const raw = (data.task ?? data) as RawHydratedAPITask
  return normalizeHydratedAPITask(raw)
}

export function useTask({
  owner,
  repo,
  taskId,
  initialTask,
  sessionsPollingInterval = 3000,
}: {
  owner?: string
  repo?: string
  taskId?: string
  initialTask?: HydratedAPITask
  sessionsPollingInterval?: number
}) {
  const queryClient = useQueryClient()
  const handleAliveUpdate = useCallback(
    (payload?: AgentSessionAlivePayload) => {
      // Only invalidate the task query if the update is related to the current task
      if (payload?.task_id === taskId) {
        queryClient.invalidateQueries({
          queryKey: agentTaskQueryKey(owner, repo, taskId),
        })
      }
    },
    [queryClient, owner, repo, taskId],
  )

  const debouncedHandleAliveUpdate = useDebounce(handleAliveUpdate, sessionsPollingInterval, {maxWait: 10000})

  const isTaskAliveEnabled =
    isFeatureEnabled('copilot_mission_control_task_alive_updates') && !!owner && !!repo && !!taskId

  useAgentSessionsAlive({
    onEvent: debouncedHandleAliveUpdate,
    enableCreateEvents: false,
    enableUpdateEvents: true,
    enableLogUpdates: false,
    enableTaskUpdates: true,
    enabled: isTaskAliveEnabled,
  })

  const query = useCAPIQuery<HydratedAPITask>({
    queryKey: agentTaskQueryKey(owner, repo, taskId),
    queryFn: () => {
      if (!owner || !repo) {
        throw new Error('owner and repo are required')
      }

      if (!taskId) {
        throw new Error('taskId is required')
      }

      return fetchTask({
        taskId,
        owner,
        repo,
      })
    },
    initialData: initialTask,
    enabled: !!taskId && !!owner && !!repo,
  })

  return {
    data: query.data,
    isLoading: query.isLoading && !query.data,
    isError: query.isError,
  }
}
